import {useState} from 'react';

import {useGetAutoDeliveryDetailsExtendedDcx, useGetAutoDeliveryDetailsExtendedDcxLazyQuery} from '@/api/consumerApi';
import appConfig from '@/config/appConfig.typed';
import {AutoDeliveryFrequency} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import iccAutoDeliveryDcxService from '@/services/icc/iccAutoDeliveryDcxService';

import {RESCHEDULE_STEPS, type RescheduleStep} from '../reschedulePopupSteps';

type UseReschedulePopupFlowReturn = {
    step: RescheduleStep;
    hasFrequencyStep: boolean;
    selectedFrequency: AutoDeliveryFrequency | null;
    selectedDate: Date | null;
    isSubmitting: boolean;
    handleClose: () => void;
    handleBack: () => void;
    handleFrequencySelect: (frequency: AutoDeliveryFrequency) => void;
    handleDateSelect: (date: Date | null) => void;
    handleCalendarConfirm: () => void;
    handleConfirm: () => void;
};

interface UseReschedulePopupFlowParams {
    onClose?: () => void;
}

export const useReschedulePopupFlow = ({onClose}: UseReschedulePopupFlowParams = {}): UseReschedulePopupFlowReturn => {
    const {skipFrequencyEnabled} = appConfig.getAutoDeliverySettings() || {};
    const hasFrequencyStep = !skipFrequencyEnabled;
    const initialStep = hasFrequencyStep ? RESCHEDULE_STEPS.FREQUENCY : RESCHEDULE_STEPS.CALENDAR;
    const [step, setStep] = useState<RescheduleStep>(initialStep);
    const [selectedFrequency, setSelectedFrequency] = useState<AutoDeliveryFrequency | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [refetchAutoDeliveryDetailsExtendedDcx] = useGetAutoDeliveryDetailsExtendedDcxLazyQuery();
    const {data: autoDeliveryDetailsExtendedDcx} = useGetAutoDeliveryDetailsExtendedDcx();
    const {nextOrderMinStartDate} = autoDeliveryDetailsExtendedDcx?.order || {};
    const currentFrequency = autoDeliveryDetailsExtendedDcx?.autoDelivery?.frequency;

    const resetFlow = () => {
        setStep(initialStep);
        setSelectedFrequency(null);
        setSelectedDate(null);
        setIsSubmitting(false);
    };

    const handleClose = () => {
        onClose?.();
        resetFlow();
    };

    const handleBack = () => {
        if (step === RESCHEDULE_STEPS.CONFIRMATION) {
            setStep(RESCHEDULE_STEPS.CALENDAR);

            return;
        }

        if (step === RESCHEDULE_STEPS.CALENDAR) {
            if (hasFrequencyStep) {
                setStep(RESCHEDULE_STEPS.FREQUENCY);
            } else {
                handleClose();
            }

            return;
        }

        handleClose();
    };

    const handleFrequencySelect = (frequency: AutoDeliveryFrequency) => {
        setSelectedFrequency(frequency);
        setStep(RESCHEDULE_STEPS.CALENDAR);
    };

    const handleDateSelect = (date: Date | null) => {
        setSelectedDate(date);
    };

    const handleCalendarConfirm = () => {
        if (!selectedDate) {
            return;
        }

        setStep(RESCHEDULE_STEPS.CONFIRMATION);
    };

    const handleConfirm = () => {
        if (!selectedDate || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        const minStartTime = nextOrderMinStartDate ? new Date(nextOrderMinStartDate).getTime() : null;
        const useMinStartDate = nextOrderMinStartDate && minStartTime != null && selectedDate.getTime() < minStartTime;
        const startDate = useMinStartDate ? nextOrderMinStartDate : selectedDate.toISOString();

        iccAutoDeliveryDcxService.rescheduleDelivery({
            startDate,
            frequency: selectedFrequency ?? currentFrequency,
            onSuccess: async () => {
                try {
                    await refetchAutoDeliveryDetailsExtendedDcx().unwrap();
                    setStep(RESCHEDULE_STEPS.SUCCESS);
                } catch {
                    setStep(RESCHEDULE_STEPS.ERROR);
                } finally {
                    setIsSubmitting(false);
                }
            },
            onError: () => {
                setStep(RESCHEDULE_STEPS.ERROR);
                setIsSubmitting(false);
            },
        });
    };

    return {
        step,
        hasFrequencyStep,
        selectedFrequency,
        selectedDate,
        isSubmitting,
        handleClose,
        handleBack,
        handleFrequencySelect,
        handleDateSelect,
        handleCalendarConfirm,
        handleConfirm,
    };
};
